import styled, { css } from 'styled-components';

import { mediaUp, rem } from 'approot/shared/style';
import {
  baseBoxShadowFocus,
  borderRadius16,
  borderRadius8,
} from 'approot/shared/styles/borders.style';
import {
  coolGrey1,
  coolGrey2,
  grey1,
  grey4,
  grey7,
  grey9,
  purple1,
} from 'approot/shared/styles/colours.style';
import { spacing8, spacing16 } from 'approot/shared/styles/spacing.style';
import {
  AnchorStyled,
  font14,
  fontSize12,
  fontSize12LineHeight,
  fontSize16,
  fontSize16LineHeight,
  fontWeightMedium,
} from 'approot/shared/styles/typography.style';
import {
  ImageStaticWrapper,
  ImageThumbnailWrapper,
} from 'approot/shared/images/images.style';

type StimulusItemThumbnailProps = {
  $withMediaPreview?: boolean;
};
export const StimulusItemThumbnail = styled.div<StimulusItemThumbnailProps>`
  ${ImageThumbnailWrapper} {
    border: none;
    height: 100%;
  }
  background-color: ${coolGrey2};
  border-radius: ${rem(borderRadius8)};
  overflow: hidden;
  height: ${rem(64)};
  flex-shrink: 0;
  margin-right: 0;
  min-width: ${rem(72)};
  position: relative;
  width: ${rem(72)};

  ${mediaUp.sm<StimulusItemThumbnailProps>`
    ${({ $withMediaPreview }) =>
      !$withMediaPreview &&
      `
      height: ${rem(72)};
      min-width: ${rem(96)};
      width: ${rem(96)};
    `}
  `}
`;

export const StimulusItemIcon = styled.div`
  background-color: ${grey1};
  border-radius: ${rem(32)};
  border: 1px solid ${grey4};
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${rem(32)};
  height: ${rem(32)};
  position: absolute;
  left: ${rem(spacing8)};
  top: ${rem(spacing8)};
`;

type StimulusItemDetailsProps = {
  $noPadding?: boolean;
};
export const StimulusItemDetails = styled.div<StimulusItemDetailsProps>`
  overflow: hidden;
  flex-grow: 1;

  ${({ $noPadding }) =>
    !$noPadding &&
    `
    padding-left: ${rem(spacing16)};
    padding-right: ${rem(spacing16)};
  `}
`;

export const StimulusItemPreTitle = styled.div`
  color: ${grey7};
  font-size: ${rem(fontSize12)};
  line-height: ${rem(fontSize12LineHeight)};
`;

export const StimulusItemTitle = styled.div`
  font-weight: ${fontWeightMedium};
  line-height: ${rem(fontSize16LineHeight)};
`;

export const StimulusItemTitleLink = styled(AnchorStyled)`
  text-decoration: none;
  svg {
    margin-left: ${rem(spacing8)};
    stroke: ${grey9};
  }
`;

export const StimulusItemText = styled.div`
  color: ${grey7};
  ${font14}

  & > p:last-child {
    margin-bottom: 0;
  }
`;

export const StimulusItemActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  z-index: 2;
`;

export const StimulusItemWrapper = styled.div`
  border-radius: ${rem(borderRadius16)};
  display: flex;
  padding: ${rem(spacing8)};
`;

type StimulusTriggerProps = {
  $isActive?: boolean;
};

const baseStimulusTrigger = css<StimulusTriggerProps>`
  background: transparent;
  border: none;
  left: 0;
  margin: 0;
  position: absolute;
  bottom: 0;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1;

  &:focus {
    outline: 0;
    & + ${StimulusItemWrapper} {
      box-shadow: inset ${baseBoxShadowFocus};
    }
  }

  &:hover {
    cursor: pointer;

    & + ${StimulusItemWrapper} {
      background-color: ${coolGrey1};

      ${StimulusItemTitle} {
        text-decoration: underline;
      }
    }
  }
`;

export const StimulusItemTrigger = styled.a<StimulusTriggerProps>`
  ${baseStimulusTrigger}
`;

export const StimulusItemButton = styled.button<StimulusTriggerProps>`
  ${baseStimulusTrigger}
`;

export const StimulusItem = styled.div<StimulusTriggerProps>`
  color: ${grey9};
  font-size: ${rem(fontSize16)};
  margin-bottom: ${rem(spacing8)};
  position: relative;
  width: 100%;

  ${({ $isActive }) =>
    $isActive &&
    `
    ${StimulusItemWrapper} {
      background-color: ${purple1};
    }
    &:hover {
      ${StimulusItemWrapper} {
        background-color: ${purple1};
      }
    }
  `}
`;

export const StimulusImage = styled.div`
  ${ImageStaticWrapper} {
    background-color: ${grey1};
    border-radius: ${rem(borderRadius8)};
    margin-bottom: ${rem(spacing8)};
    min-height: ${rem(230)};
    text-align: center;
  }
`;
